import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const NUMS = ["base_salary", "fixed_allowance", "nonfixed_allowance", "thr", "overtime_hours", "overtime_multiplier", "overtime_manual",
  "bpjs_jkk", "bpjs_jkm", "bpjs_kes_company", "bpjs_jht_employee", "bpjs_jp_employee", "bpjs_kes_employee", "bpjs_lump_company", "bpjs_lump_employee",
  "insurance_company", "insurance_employee"];
const MANUAL = [["bpjs_jkk", "JKK (Perusahaan)"], ["bpjs_jkm", "JKM (Perusahaan)"], ["bpjs_kes_company", "BPJS Kesehatan (Perusahaan)"],
  ["bpjs_jht_employee", "JHT (Karyawan)"], ["bpjs_jp_employee", "JP (Karyawan)"], ["bpjs_kes_employee", "BPJS Kesehatan (Karyawan)"]];

const initFrom = (rec) => {
  const f = Object.fromEntries(NUMS.map((k) => [k, rec?.[k] ?? ""]));
  return { ...f, overtime_multiplier: rec?.overtime_multiplier ?? 1.5, bpjs_mode: rec?.bpjs_mode === "manual" ? "manual" : "auto",
    bpjs_tk: rec?.bpjs_tk !== false, bpjs_jp: rec?.bpjs_jp !== false, bpjs_kes: rec?.bpjs_kes !== false, bpjs_jkk_rate: rec?.bpjs_jkk_rate ?? "",
    loan_deduction: rec?.id ? (rec.loan_deduction ?? 0) : "", employee_id: rec?.employee_id || "",
    work_days: rec?.work_days ?? "", present_days: rec?.present_days ?? "" };
};

function Num({ f, setF, k, label, hint }) {
  return (
    <div className="space-y-1.5"><Label className="text-xs">{label}</Label>
      <Input type="number" className="num h-9" value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`row-${k.replace(/_/g, "-")}`} />
      {hint && <p className="text-[10px] text-muted-foreground">{hint}</p>}</div>
  );
}

function Box({ title, children }) {
  return <div className="col-span-2 rounded-md border border-border p-3"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">{title}</p><div className="grid grid-cols-3 gap-3">{children}</div></div>;
}

export function RowForm({ clientId, year, month, rec, employees = [], usedIds = [], onDone, trigger }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(initFrom(rec));
  useEffect(() => { if (open) setF(initFrom(rec)); }, [open, rec]);
  const choices = employees.filter((e) => !usedIds.includes(e.id));
  const save = async () => {
    const p = { bpjs_mode: f.bpjs_mode, bpjs_tk: f.bpjs_tk, bpjs_jp: f.bpjs_jp, bpjs_kes: f.bpjs_kes,
      bpjs_jkk_rate: f.bpjs_jkk_rate === "" ? null : Number(f.bpjs_jkk_rate) };
    NUMS.forEach((k) => { p[k] = Number(f[k] || 0); });
    p.overtime_multiplier = Number(f.overtime_multiplier || 1.5);
    if (f.loan_deduction !== "" && f.loan_deduction != null) p.loan_deduction = Number(f.loan_deduction);
    p.work_days = f.work_days === "" ? null : Number(f.work_days);
    p.present_days = f.present_days === "" ? null : Number(f.present_days);
    try {
      if (rec?.id) await api.put(`/payroll/${rec.id}`, p);
      else {
        if (!f.employee_id) { toast.error("Pilih karyawan"); return; }
        await api.post("/payroll/rows", { ...p, client_id: clientId, year, month, employee_id: f.employee_id });
      }
      toast.success("Transaksi PPh 21 disimpan & dihitung"); setOpen(false); onDone();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const flag = (k, l) => <label className="flex items-center gap-2 text-xs"><Checkbox checked={!!f[k]} onCheckedChange={(v) => setF({ ...f, [k]: !!v })} data-testid={`row-${k.replace(/_/g, "-")}`} />{l}</label>;
  const hasLump = Number(f.bpjs_lump_company || 0) || Number(f.bpjs_lump_employee || 0);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto">
        <DialogHeader><DialogTitle>{rec?.id ? `Edit Transaksi — ${rec.name}` : "Tambah Transaksi Karyawan"}</DialogTitle>
          <DialogDescription className="text-xs">Bruto dihitung otomatis: gaji + tunjangan + lembur + THR + JKK + JKM + BPJS Kesehatan perusahaan + asuransi swasta.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {!rec?.id && (
            <div className="col-span-2 space-y-1.5"><Label className="text-xs">Karyawan</Label>
              <Select value={f.employee_id} onValueChange={(v) => setF({ ...f, employee_id: v })}>
                <SelectTrigger data-testid="row-employee"><SelectValue placeholder="Pilih karyawan" /></SelectTrigger>
                <SelectContent>{choices.map((e) => <SelectItem key={e.id} value={e.id}>{e.employee_code ? `${e.employee_code} · ` : ""}{e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <Box title="Penghasilan">
            <Num f={f} setF={setF} k="base_salary" label="Gaji Pokok" />
            <Num f={f} setF={setF} k="fixed_allowance" label="Tunjangan Tetap" />
            <Num f={f} setF={setF} k="nonfixed_allowance" label="Tunjangan Tidak Tetap" />
            <Num f={f} setF={setF} k="thr" label="THR / Bonus" />
          </Box>
          <Box title="Kehadiran (potongan sebelum pajak)">
            <Num f={f} setF={setF} k="work_days" label="Hari Kerja" hint={`Kosong = otomatis${rec?.work_days_auto ? ` (${rec.work_days_auto} hari)` : ""}`} />
            <Num f={f} setF={setF} k="present_days" label="Hari Masuk" hint="Kosong = masuk penuh" />
            <div className="text-[10px] text-muted-foreground self-end pb-2">Potongan = (gaji pokok + tunj. tetap) ÷ hari kerja × hari tidak masuk{rec?.absence_deduction ? ` · saat ini Rp ${Number(rec.absence_deduction).toLocaleString("id-ID")}` : ""}</div>
          </Box>
          <Box title="Uang Lembur">
            <Num f={f} setF={setF} k="overtime_hours" label="Jam Lembur" hint="Gaji pokok ÷ 173 × jam × pengali" />
            <Num f={f} setF={setF} k="overtime_multiplier" label="Pengali" />
            <Num f={f} setF={setF} k="overtime_manual" label="Nominal Manual" hint="Jika diisi, menggantikan hitungan otomatis" />
          </Box>
          <div className="col-span-2 rounded-md border border-border p-3 space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">BPJS</p>
              <Select value={f.bpjs_mode} onValueChange={(v) => setF({ ...f, bpjs_mode: v })}>
                <SelectTrigger className="w-64 h-9" data-testid="row-bpjs-mode"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="auto">Otomatis (rate × gaji pokok)</SelectItem><SelectItem value="manual">Manual (isi nominal)</SelectItem></SelectContent>
              </Select>
            </div>
            {f.bpjs_mode === "auto" ? (
              <div className="flex flex-wrap items-end gap-4">
                {flag("bpjs_tk", "JHT, JKK, JKM")}{flag("bpjs_jp", "JP")}{flag("bpjs_kes", "BPJS Kesehatan")}
                <div className="w-40"><Num f={f} setF={setF} k="bpjs_jkk_rate" label="Tarif JKK (%)" /></div>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-3">
                {MANUAL.map(([k, l]) => <Num key={k} f={f} setF={setF} k={k} label={l} />)}
                {hasLump ? <><Num f={f} setF={setF} k="bpjs_lump_company" label="Premi BPJS lain (Perusahaan)" /><Num f={f} setF={setF} k="bpjs_lump_employee" label="Iuran BPJS lain (Karyawan)" /></> : null}
              </div>
            )}
          </div>
          <Box title="Asuransi Swasta & Potongan">
            <Num f={f} setF={setF} k="insurance_company" label="Premi Perusahaan" hint="Menambah bruto" />
            <Num f={f} setF={setF} k="insurance_employee" label="Potongan Karyawan" />
            <div className="space-y-1.5"><Label className="text-xs">Potongan Hutang</Label>
              <Input type="number" className="num h-9" placeholder="otomatis dari cicilan" value={f.loan_deduction} onChange={(e) => setF({ ...f, loan_deduction: e.target.value })} data-testid="row-loan-deduction" />
              <p className="text-[10px] text-muted-foreground">Dipotong setelah PPh 21</p></div>
          </Box>
        </div>
        <DialogFooter><Button onClick={save} data-testid="row-save">Simpan & Hitung</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
